import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Search, 
  FileCheck, 
  File, 
  BookOpen, 
  Shield, 
  Gavel, 
  PenTool, 
  Calculator,
  ArrowRight,
  CheckCircle,
  Zap,
  Globe,
  Users
} from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: FileText,
      title: 'Document Analysis',
      description: 'Instantly analyze contracts, agreements, and legal documents with AI-powered insights and risk assessment.',
      features: ['Contract review and redlining', 'Risk identification', 'Clause extraction']
    },
    {
      icon: Search,
      title: 'Legal Research',
      description: 'Accelerate legal research with AI that understands context and delivers relevant case law and precedents.',
      features: ['Case law discovery', 'Precedent analysis', 'Jurisdiction research']
    },
    {
      icon: FileCheck,
      title: 'Case Summarization',
      description: 'Generate comprehensive case summaries and extract key insights from complex legal proceedings.',
      features: ['Case brief generation', 'Key fact extraction', 'Timeline creation']
    },
    {
      icon: PenTool,
      title: 'Legal Writing',
      description: 'Enhance your legal writing with AI assistance for motions, briefs, and correspondence.',
      features: ['Motion drafting', 'Brief assistance', 'Letter templates']
    },
    {
      icon: Shield,
      title: 'Compliance Check',
      description: 'Ensure regulatory compliance across multiple jurisdictions with automated compliance monitoring.',
      features: ['Regulatory updates', 'Compliance scoring', 'Risk alerts']
    },
    {
      icon: Calculator,
      title: 'Due Diligence',
      description: 'Streamline due diligence processes with comprehensive document review and analysis.',
      features: ['Document classification', 'Data extraction', 'Report generation']
    }
  ];

  const stats = [
    { label: 'Legal Professionals', value: '10,000+' },
    { label: 'Documents Analyzed', value: '1M+' },
    { label: 'Time Saved', value: '500K+ Hours' },
    { label: 'Accuracy Rate', value: '99.7%' }
  ];

  return (
    <div className="homepage">
      {/* Header */}
      <header className="homepage-header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <div className="logo-icon">⚖️</div>
              <span className="logo-text">S5 Kaizen Legal</span>
            </div>
            <nav className="header-nav">
              <button 
                className="btn btn-ghost"
                onClick={() => navigate('/login')}
              >
                Sign In
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/signup')}
              >
                Get Started
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              AI-Powered Legal Intelligence
            </h1>
            <p className="hero-subtitle">
              Transform your legal practice with cutting-edge AI tools designed for 
              modern legal professionals worldwide.
            </p>
            <div className="hero-cta">
              <button 
                className="btn btn-primary btn-large"
                onClick={() => navigate('/signup')}
              >
                Start Free Trial
                <ArrowRight size={20} />
              </button>
              <button 
                className="btn btn-secondary btn-large"
                onClick={() => navigate('/login')}
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="value-section">
        <div className="container">
          <h2 className="section-title">Revolutionize Your Legal Practice</h2>
          <p className="section-subtitle">
            Leverage the power of AI to streamline your workflow, enhance research capabilities, 
            and deliver superior client outcomes.
          </p>
          
          <div className="value-grid">
            <div className="value-item">
              <Zap className="value-icon" />
              <h3>10x Faster Research</h3>
              <p>Accelerate legal research with AI-powered analysis and insights</p>
            </div>
            <div className="value-item">
              <CheckCircle className="value-icon" />
              <h3>99.7% Accuracy</h3>
              <p>Industry-leading accuracy in document analysis and risk assessment</p>
            </div>
            <div className="value-item">
              <Globe className="value-icon" />
              <h3>Global Coverage</h3>
              <p>Comprehensive legal intelligence across multiple jurisdictions</p>
            </div>
            <div className="value-item">
              <Users className="value-icon" />
              <h3>Trusted by 10K+</h3>
              <p>Join thousands of legal professionals who trust our platform</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="features-section">
        <div className="container">
          <h2 className="section-title">Comprehensive Legal AI Tools</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">
                  <feature.icon size={32} />
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                <ul className="feature-list">
                  {feature.features.map((item, idx) => (
                    <li key={idx}>• {item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Transform Your Practice?</h2>
            <p className="cta-subtitle">
              Join thousands of legal professionals who trust S5 Kaizen Legal for their 
              AI-powered legal solutions.
            </p>
            <button 
              className="btn btn-primary btn-large"
              onClick={() => navigate('/signup')}
            >
              Get Started Today
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="homepage-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <div className="logo-icon">⚖️</div>
              <span className="logo-text">S5 Kaizen Legal</span>
            </div>
            <p className="footer-text">
              Empowering legal professionals with AI-driven intelligence and automation.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
